import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  SupportSavedReplyInputSchema,
  type SupportSavedReply,
} from "../../shared/support-saved-replies";
import { ContentLoading } from "../components/ContentLoading";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { Textarea } from "../components/ui/textarea";
import { LabeledInput } from "./support-fields";
import { deleteSavedReply, listSavedReplies, saveSavedReply, supportKeys } from "./support-api";

// ADR-0089: saved replies the team inserts into a reply and edits before sending.

type Editing = { id: string | null; title: string; bodyText: string };

function useRefresh() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: supportKeys.savedReplies() });
}

function Editor({ editing, onDone }: { editing: Editing; onDone: () => void }) {
  const refresh = useRefresh();
  const [draft, setDraft] = useState(editing);
  const parsed = SupportSavedReplyInputSchema.safeParse({
    title: draft.title,
    bodyText: draft.bodyText,
  });
  const save = useMutation({
    mutationFn: () => saveSavedReply(draft.id, { title: draft.title, bodyText: draft.bodyText }),
    onSuccess: async () => {
      await refresh();
      onDone();
    },
  });
  const field = draft.id ?? "new";
  return (
    <form
      aria-label={draft.id === null ? "New saved reply" : `Edit ${editing.title}`}
      className="flex flex-col gap-3 rounded-md border p-4"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <LabeledInput
        id={`saved-reply-title-${field}`}
        label="Title"
        value={draft.title}
        disabled={save.isPending}
        onChange={(title) => {
          setDraft({ ...draft, title });
        }}
      />
      <label
        className="flex flex-col gap-1 text-sm font-medium"
        htmlFor={`saved-reply-body-${field}`}
      >
        Reply text
        <Textarea
          id={`saved-reply-body-${field}`}
          rows={5}
          value={draft.bodyText}
          disabled={save.isPending}
          onChange={(event) => {
            setDraft({ ...draft, bodyText: event.target.value });
          }}
        />
      </label>
      {save.error ? <FieldError>{save.error.message}</FieldError> : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={!parsed.success || save.isPending}>
          {save.isPending ? "Saving…" : "Save"}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function ReplyRow({ reply, onEdit }: { reply: SupportSavedReply; onEdit: () => void }) {
  const refresh = useRefresh();
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation({ mutationFn: () => deleteSavedReply(reply.id), onSuccess: refresh });
  return (
    <li className="flex flex-wrap items-start justify-between gap-3 border-b border-muted py-3 last:border-b-0">
      <span className="min-w-0 flex-1">
        <span className="block font-semibold">{reply.title}</span>
        <span className="line-clamp-2 block text-sm whitespace-pre-line text-muted-foreground">
          {reply.bodyText}
        </span>
      </span>
      {confirming ? (
        <span className="flex items-center gap-2">
          <span className="text-sm">Delete “{reply.title}”?</span>
          <Button
            type="button"
            variant="destructive"
            disabled={remove.isPending}
            onClick={() => {
              remove.mutate();
            }}
          >
            Delete
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setConfirming(false);
            }}
          >
            Keep
          </Button>
        </span>
      ) : (
        <span className="flex gap-2">
          <Button type="button" variant="outline" onClick={onEdit}>
            Edit
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setConfirming(true);
            }}
          >
            Delete
          </Button>
        </span>
      )}
      {remove.error ? <FieldError>{remove.error.message}</FieldError> : null}
    </li>
  );
}

/** The saved replies, with adding, editing, and deleting. */
export function SupportSavedReplies() {
  const replies = useQuery({ queryKey: supportKeys.savedReplies(), queryFn: listSavedReplies });
  const [editing, setEditing] = useState<Editing | null>(null);
  const done = () => {
    setEditing(null);
  };
  const list = replies.data?.replies ?? [];
  return (
    <div className="flex flex-col gap-3 rounded-xl bg-card p-6 shadow-card">
      <p className="text-sm text-muted-foreground">
        Write {"{{first_name}}"} where the requester’s first name should go.
      </p>
      {replies.isPending ? <ContentLoading label="Loading saved replies…" variant="rows" /> : null}
      {replies.error ? <FieldError>{replies.error.message}</FieldError> : null}
      {editing?.id === null ? <Editor editing={editing} onDone={done} /> : null}
      {replies.data && list.length === 0 && editing === null ? (
        <p className="text-muted-foreground">No saved replies yet.</p>
      ) : null}
      {list.length > 0 ? (
        <ul aria-label="Saved replies">
          {list.map((reply) =>
            editing?.id === reply.id ? (
              <li key={reply.id}>
                <Editor editing={editing} onDone={done} />
              </li>
            ) : (
              <ReplyRow
                key={reply.id}
                reply={reply}
                onEdit={() => {
                  setEditing({ id: reply.id, title: reply.title, bodyText: reply.bodyText });
                }}
              />
            ),
          )}
        </ul>
      ) : null}
      {editing === null ? (
        <div>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setEditing({ id: null, title: "", bodyText: "" });
            }}
          >
            Add a saved reply
          </Button>
        </div>
      ) : null}
    </div>
  );
}
